import { useState, useRef, useCallback, useMemo, useEffect } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;
export const MAX_EMAILS = 10;

const isValidEmail = (value) => EMAIL_REGEX.test((value || '').trim());
const isPasswordLongEnough = (value) => (value || '').trim().length >= MIN_PASSWORD_LENGTH;

export const useExportAllWithEmailHook = (props) => {
    const { open = false, onClose, onExport } = props;

    const [emails, setEmails] = useState(['']);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const modalRef = useRef(null);

    /**
     * Sync with frontend-commons Modal: call toggleModal when open becomes true
     * so the modal actually opens (Modal is controlled via ref, not only open prop).
     */
    useEffect(() => {
        if (open && modalRef.current?.toggleModal) {
            modalRef.current.toggleModal();
        }
    }, [open]);

    const handleAddEmail = useCallback(() => {
        setEmails((prev) => (prev.length >= MAX_EMAILS ? prev : [...prev, '']));
    }, []);

    const handleEmailChange = useCallback((index, value) => {
        setEmails((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }, []);

    const handleRemoveEmail = useCallback((index) => {
        setEmails((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    }, []);

    const handleClose = useCallback(() => {
        if (modalRef.current?.toggleModal) {
            modalRef.current.toggleModal();
        }
        setEmails(['']);
        setPassword('');
        setShowPassword(false);
        onClose?.();
    }, [onClose]);

    const hasAtLeastOneEmail = useMemo(
        () => emails.some((e) => isValidEmail(e)),
        [emails],
    );
    const isPasswordValid = useMemo(
        () => (password || '').trim().length >= MIN_PASSWORD_LENGTH,
        [password],
    );
    const isFormValid = hasAtLeastOneEmail && isPasswordValid;

    const passwordError = useMemo(() => {
        const trimmed = (password || '').trim();
        if (trimmed.length === 0) return null;
        return isPasswordLongEnough(password) ? null : `Must be at least ${MIN_PASSWORD_LENGTH} characters long`;
    }, [password]);

    const handleContinue = useCallback(() => {
        if (!isFormValid) return;
        const validEmails = emails.filter((e) => isValidEmail(e));
        onExport?.({ emails: validEmails, password: password.trim() });
    }, [isFormValid, emails, password, onExport]);

    const handlePasswordChange = useCallback((e) => {
        setPassword(e.target.value);
    }, []);

    const handleToggleShowPassword = useCallback(() => {
        setShowPassword((p) => !p);
    }, []);

    return {
        modalRef,
        emails,
        password,
        showPassword,
        isFormValid,
        passwordError,
        handleAddEmail,
        handleEmailChange,
        handleRemoveEmail,
        handleClose,
        handleContinue,
        handlePasswordChange,
        handleToggleShowPassword,
        isValidEmail,
    };
};
